// ── Athena Thinking States — real, non-repeating, context-aware ──────────────

const SIMPLE_RE = /^(oi|ol[aá]|bom\s*dia|boa\s*tarde|boa\s*noite|madrugada|tudo\s*(bem|bom|certo|ok)|beleza|sim|n[aã]o|ok|certo|legal|show|blz|valeu|obrigad[ao]|boa|e\s*a[ií]|continua|entendi|perfeito|vlw|oi\s+athena|bom\s+dia\s+athena|boa\s+tarde\s+athena|pode\s+continuar|continue|claro|top|massa)[!?\.]*$/i;

const BIBLE_RE = /b[íi]blia|b[íi]blico|vers[íi]culo|salmo|evangelho|g[êe]nesis|apocalipse|mateus|lucas|jo[aã]o|romanos|cor[íi]n|passagem\s+b[íi]blic|estudo\s+b[íi]blic/i;

const CODE_EXTENSIONS = /\.(js|ts|jsx|tsx|py|html|css|json|yml|yaml|sh|sql|md|php|rb|go|rs|c|cpp|h|vue|svelte)$/i;

// ── Fresh information detector ────────────────────────────────────────────────
const FRESH_INFO_RE = /hoje|agora\s+mesmo|recente|[uú]ltimo[a]?s?|not[íi]cias?|evento\s+astron|astron[oô]m|clima\s+hoje|resultado\s+(de\s+hoje|do\s+jogo)|cota[cç][aã]o|pre[cç]o\s+atual|agenda\s+de\s+hoje|aconteceu\s+hoje|saiu\s+hoje|lan[cç]amento\s+de\s+hoje|[uú]ltimas?\s+horas?|essa\s+semana|est[aá]\s+acontecendo|j[aá]\s+lan[cç]ou|voc[eê]\s+viu/i;

const LOCATION_RE = /aqui|minha\s+cidade|hoje\s+[aà]\s+noite|c[eé]u\s+(hoje|desta|de\s+hoje)|vis[íi]vel\s+(em|para|aqui)|perto\s+de\s+mim|evento\s+astron|alinhamento|conj[uú]n[cç][aã]o\s+de|eclipse/i;

export function requiresFreshInfo(message) {
  return FRESH_INFO_RE.test((message || '').trim());
}

export function requiresLocation(message) {
  const t = (message || '').trim();
  return LOCATION_RE.test(t) && FRESH_INFO_RE.test(t);
}

// ── Detect thinking type ──────────────────────────────────────────────────────
export function detectThinkingType(message, attachments = []) {
  if (attachments.some(a => a.type?.startsWith('image/'))) return 'IMAGEM';
  if (attachments.some(a => a.type?.includes('text') || CODE_EXTENSIONS.test(a.name || ''))) return 'CODIGO';
  if (attachments.length > 0) return 'PROJETO';

  const t = (message || '').toLowerCase();

  if (/c[oó]digo|code|fun[cç][aã]o|script|bug|erro|error|debug|programar|desenvolver|implementar|refatorar|classe|m[eé]todo|componente|api|endpoint|backend|frontend/.test(t)) return 'CODIGO';

  if (requiresFreshInfo(message)) return 'PESQUISA';

  if (/pesquis|busca|buscar|procura|referencia|mercado|concorrente|benchmark|dados|estat[íi]stic|informa[cç][oõ]es|not[íi]cias|pre[cç]o|tendencia|tend[êe]ncia/.test(t)) return 'PESQUISA';

  if (/projeto|athena|fase|arquitetura|sistema|plano|planejamento|estrat[eé]gia|roadmap|sprint|tarefa|backlog|release|deploy|campanha|produto/.test(t)) return 'PROJETO';

  return 'CONVERSA';
}

// ── Real thinking steps — each shown once, no loops ──────────────────────────
export function getThinkingMessages(message = '', type = 'CONVERSA', cogMode = 'basico') {
  const t = (message || '').toLowerCase().trim();
  const len = t.length;

  // Saudações e mensagens muito curtas
  if (!t || SIMPLE_RE.test(t) || len <= 10) return ['Respondendo…'];

  // Bíblia / estudo
  if (BIBLE_RE.test(t)) return ['Analisando o trecho…', 'Conectando contexto e aplicação…', 'Organizando o estudo…'];

  // Por tipo de anexo / detecção
  if (type === 'IMAGEM') return ['Analisando a imagem…', 'Identificando o que está visível…', 'Organizando resposta…'];

  if (type === 'CODIGO') return ['Verificando o problema…', 'Pensando na solução…', 'Estruturando os próximos passos…'];

  // Informação recente com localização
  if (requiresFreshInfo(message) && requiresLocation(message)) {
    return ['Interpretando sua pergunta…', 'Verificando localização…', 'Pesquisando informações recentes…', 'Organizando resposta…'];
  }

  // Informação recente sem localização
  if (type === 'PESQUISA' || requiresFreshInfo(message)) {
    return ['Interpretando sua pergunta…', 'Pesquisando informações recentes…', 'Organizando resposta…'];
  }

  if (type === 'PROJETO') return ['Consultando contexto do projeto…', 'Conectando com o que já foi discutido…', 'Montando o plano…'];

  if (cogMode === 'foco_total') return ['Consultando contexto anterior…', 'Conectando com a missão…', 'Preparando próxima etapa…'];

  if (cogMode === 'continuo') return ['Consultando contexto anterior…', 'Conectando com o fio do projeto…', 'Preparando resposta…'];

  // Mensagem longa
  if (len > 200) return ['Interpretando sua mensagem…', 'Separando os pontos principais…', 'Organizando resposta…'];

  // Mensagem média
  if (len > 60) return ['Interpretando sua pergunta…', 'Consultando contexto anterior…', 'Organizando resposta…'];

  // Mensagem simples / curta
  return ['Interpretando sua mensagem…', 'Preparando resposta…'];
}

// ── Legacy exports (backward compat) ─────────────────────────────────────────
export const THINKING_STATES = {
  CONVERSA: ['Interpretando sua mensagem…', 'Preparando resposta…'],
  IMAGEM:   ['Analisando a imagem…', 'Identificando o que está visível…', 'Organizando resposta…'],
  CODIGO:   ['Verificando o problema…', 'Pensando na solução…', 'Estruturando os próximos passos…'],
  PROJETO:  ['Consultando contexto do projeto…', 'Conectando ideias…', 'Montando o plano…'],
  PESQUISA: ['Interpretando sua pergunta…', 'Pesquisando informações recentes…', 'Organizando resposta…'],
};
